/**
 * ¿De dónde entraron los leads de un estado? (ej. No aplica / No califica)
 * Datos: stateEntrySources del período (Creado at).
 * @module-isolated analytics-leads
 */
import { useMemo, useState } from 'react';
import { SectionHeader } from '../../../shared/components/index.js';

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('es-CL');
}

function pickDefaultState(list) {
  if (!list || !list.length) return '';
  const prefer = list.find(
    (s) =>
      s.resultado === 'no_aplica' ||
      /no\s*aplica|no\s*califica|descalif/i.test(String(s.name || ''))
  );
  if (prefer) return prefer.stateId;
  return list[0].stateId;
}

export default function StateEntrySourcesPanel({ stateEntrySources, periodLabel }) {
  const list = Array.isArray(stateEntrySources) ? stateEntrySources : [];
  const [stateId, setStateId] = useState(() => pickDefaultState(list));

  // Si llega data nueva y el id elegido no existe, re-pick.
  const selectedId = useMemo(() => {
    if (!list.length) return '';
    if (list.some((s) => s.stateId === stateId)) return stateId;
    return pickDefaultState(list);
  }, [list, stateId]);

  const selected = list.find((s) => s.stateId === selectedId) || null;

  if (!list.length) {
    return (
      <section className="analytics-section">
        <SectionHeader
          title="¿De dónde entraron? (por estado)"
          note={
            'Elegí un estado Kommo/GI (ej. No aplica). Solo leads creados en ' +
            (periodLabel || 'el período') +
            '.'
          }
        />
        <p className="analytics-empty">
          Sin leads creados en este período (o sin estados mapeados). Probá otro rango o
          sincronizá Kommo.
        </p>
      </section>
    );
  }

  const campaigns = (selected && selected.campaigns) || [];
  const otherOrigins = (selected && selected.otherOrigins) || [];
  const stages = (selected && selected.kommoStages) || [];

  return (
    <section className="analytics-section">
      <SectionHeader
        title="¿De dónde entraron? (por estado)"
        note={
          'Partís del estado actual (ej. No aplica / No califica) y ves por qué campaña Meta ' +
          'entraron los leads creados en ' +
          (periodLabel || 'el período') +
          '.'
        }
      />
      <div className="analytics-panel">
        <label className="analytics-field" style={{ maxWidth: 320, marginBottom: 12 }}>
          <span>Estado</span>
          <select
            value={selectedId}
            onChange={(e) => setStateId(e.target.value)}
            aria-label="Estado interno o resultado"
          >
            {list.map((s) => (
              <option key={s.stateId} value={s.stateId}>
                {(s.name || s.stateId) +
                  ' (' +
                  fmt(s.count) +
                  ')' +
                  (s.resultado ? ' · ' + s.resultado : '')}
              </option>
            ))}
          </select>
        </label>

        {selected ? (
          <>
            <div className="analytics-kpi-grid" style={{ marginBottom: 12 }}>
              <article className="analytics-kpi-card is-info">
                <p className="analytics-kpi-label">En este estado</p>
                <p className="analytics-kpi-value">{fmt(selected.count)}</p>
                <p className="analytics-kpi-hint">Creados en el rango</p>
              </article>
              <article className="analytics-kpi-card is-ok">
                <p className="analytics-kpi-label">Venían de Meta</p>
                <p className="analytics-kpi-value">{fmt(selected.metaCount)}</p>
                <p className="analytics-kpi-hint">Con campaña / flag Meta</p>
              </article>
              <article className="analytics-kpi-card is-muted">
                <p className="analytics-kpi-label">No Meta</p>
                <p className="analytics-kpi-value">{fmt(selected.otherCount)}</p>
                <p className="analytics-kpi-hint">Otras etiquetas origen</p>
              </article>
              <article className="analytics-kpi-card is-muted">
                <p className="analytics-kpi-label">Sin origen</p>
                <p className="analytics-kpi-value">
                  {fmt((selected.noneCount || 0) + (selected.unclassifiedCount || 0))}
                </p>
                <p className="analytics-kpi-hint">Sin etiqueta / sin clasificar</p>
              </article>
            </div>

            <p className="analytics-sub" style={{ margin: '0 0 6px' }}>
              Campañas Meta (entrada)
            </p>
            {campaigns.length ? (
              <div className="analytics-table-wrap">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Campaña Meta</th>
                      <th className="num">Leads</th>
                      <th className="num">% del estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map((c) => {
                      const pct = selected.count
                        ? Math.round((c.count / selected.count) * 1000) / 10
                        : 0;
                      return (
                        <tr key={c.id || c.name}>
                          <td>
                            <strong>{c.name || c.id || '—'}</strong>
                            {c.id ? (
                              <span className="analytics-sub"> ID {c.id}</span>
                            ) : null}
                          </td>
                          <td className="num">{fmt(c.count)}</td>
                          <td className="num">{fmt(pct)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="analytics-empty">Ninguno de estos leads trae campaña Meta.</p>
            )}

            {otherOrigins.length ? (
              <>
                <p className="analytics-sub" style={{ margin: '14px 0 6px' }}>
                  Orígenes No Meta
                </p>
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Etiqueta</th>
                        <th className="num">Leads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherOrigins.map((o) => (
                        <tr key={o.tagName}>
                          <td>{o.tagName}</td>
                          <td className="num">{fmt(o.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {stages.length ? (
              <>
                <p className="analytics-sub" style={{ margin: '14px 0 6px' }}>
                  Etapa Kommo (detalle)
                </p>
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Embudo → Etapa</th>
                        <th className="num">Leads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stages.map((st) => (
                        <tr key={st.pipelineName + '|' + st.stageName}>
                          <td>
                            {st.pipelineName}
                            <span className="analytics-sub"> {st.stageName}</span>
                          </td>
                          <td className="num">{fmt(st.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
